<template>
    <div class="h-full">
        <!-- <pre class="bg-red-400 text-xs max-h-52 overflow-auto">{{ labelTree }}</pre> -->
        <h3 class="font-semibold mb-2 text-slate-300 hover:bg-white/10 hover:text-ui-active transition-colors cursor-pointer"
            :class="{ 'bg-white/15 ring-2 ring-ui-active text-ui-active': isDragOverAll }"
            @dragover="onDragOver" 
            @drop="onRootDrop"
            @dragenter="isDragOverAll = true"
            @dragleave="isDragOverAll = false"
            @click="setSelectedLabel('all')">All</h3>
        <h3 class="mb-2 text-slate-300 hover:bg-white/10 hover:text-ui-active transition-colors cursor-pointer"
            :class="{ 'bg-white/15 ring-2 ring-ui-active text-ui-active': isDragOverUnlabeled }"
            @click="setSelectedLabel('unlabeled')" 
            @dragover="onDragOver"
            @drop="onRootDrop"
            @dragenter="isDragOverUnlabeled = true"
            @dragleave="isDragOverUnlabeled = false">Unlabeled</h3>
        <div class="mb-4">
            <ul class="pl-2">
                <LabelTreeNode :editable="editable" v-for="node in labelTree" :key="node.id" :node="node"
                    :expanded="expandedNodes" :selectedLabelId="selectedLabelId" @toggle="toggleNode" @select="onSelect"
                    @delete="deleteLabelById" @image-drop="onImageDrop" @label-drop="onLabelDrop" />
            </ul>
        </div>
        <form v-if="editable" @submit.prevent="addNewLabel" class="flex items-center justify-between align-bottom">
            <input v-model="newLabelName" type="text" placeholder="Add label"
                class="p-1 w-full rounded-md border border-white/20 bg-white/15 text-white placeholder-slate-500 text-xs"
                :class="{ 'bg-red-900/40 border-red-500': labelNameExists }" />
            <button type="submit"
                class="ml-2 p-0.5 bg-white/15 text-ui-active rounded-full border border-white/20 flex items-center justify-center"
                title="Add label">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 20 20"
                    stroke="currentColor" stroke-width="2">
                    <line x1="10" y1="4" x2="10" y2="16" stroke="currentColor" stroke-linecap="round" />
                    <line x1="4" y1="10" x2="16" y2="10" stroke="currentColor" stroke-linecap="round" />
                </svg>
            </button>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import LabelTreeNode from './LabelTreeNode.vue';
import { useImageStore } from '../../stores/imageStore';

const imageStore = useImageStore();

const emit = defineEmits(['select', 'image-drop']);

const labels = ref([]);
const expandedNodes = ref(new Set());
const selectedNode = ref(null);
const selectedLabelId = ref('all');
const newLabelName = ref('');
const isDragOverAll = ref(false);
const isDragOverUnlabeled = ref(false);

const props = defineProps({
    editable: {
        type: Boolean,
        required: false,
        default: true,
    },
    refreshLabels: {
        type: Number,
        required: false,
        default: 0,
    }
});

const labelNameExists = computed(() => {
    // console.log('Checking if label name exists:', newLabelName.value);
    if (selectedNode.value) {
        const childLabels = selectedNode.value.children || [];
        return childLabels.some(label => label.name === newLabelName.value.trim());
    }
    return false;
});

onMounted(async () => {
    labels.value = await imageStore.getAllLabels();
});

watch(() => props.refreshLabels, async () => {
    labels.value = await imageStore.getAllLabels();
});


function buildLabelTree(flatLabels, parentId = null) {
    // console.log('Building label tree for parent ID:', parentId);

    // function collectDescendantIds(node) {
    //     let ids = [];
    //     if (node.children && node.children.length) {
    //         for (const child of node.children) {
    //             ids.push(child.id);
    //             ids = ids.concat(collectDescendantIds(child));
    //         }
    //     }
    //     return ids;
    // }

    function collectParentIds(label, flatLabels) {
        const parentIds = [];
        let currentParentId = label.parentId;
        while (currentParentId != null) {
            parentIds.push(currentParentId);
            const parentLabel = flatLabels.find(l => l.id === currentParentId);
            if (!parentLabel) break;
            currentParentId = parentLabel.parentId;
        }
        return parentIds;
    }

    const nodes = flatLabels
        .filter(label => label.parentId === parentId)
        .map(label => {
            const children = buildLabelTree(flatLabels, label.id);
            const node = {
                ...label,
                children: children.length ? children : [],
            };
            // node.descendantIds = collectDescendantIds(node);
            node.parentIds = collectParentIds(label, flatLabels);
            return node;
        });
    return nodes;
}

const labelTree = computed(() => buildLabelTree(labels.value));

function toggleNode(id) {
    if (expandedNodes.value.has(id)) expandedNodes.value.delete(id);
    else expandedNodes.value.add(id);
    expandedNodes.value = new Set(expandedNodes.value);
}

function onSelect(label) {
    selectedNode.value = label;
    selectedLabelId.value = label.id;
    emit('select', label);
}

function setSelectedLabel(labelId) {
    selectedLabelId.value = labelId;
    emit('select', { id: labelId, name: labelId === 'all' ? 'All' : 'Unlabeled' });
}

// Drag and drop support
async function onImageDrop({ labelId, imageId }) {
    emit('image-drop', { labelId, imageId });
}

async function onLabelDrop({ labelId, parentId }) {
    await imageStore.updateLabelParent(labelId, parentId);
    labels.value = await imageStore.getAllLabels();
}

// Handle drop on root dropzone (Unlabeled)
function onRootDrop(e) {
    isDragOverAll.value = false;
    isDragOverUnlabeled.value = false;
    const drag_imageId = e.dataTransfer.getData('image-id');
    const drag_labelId = e.dataTransfer.getData('label-id');
    if (drag_imageId) {
        emit('image-drop', { labelId: null, imageId: drag_imageId });
    } else if (drag_labelId) {
        // Move label to root (parentId = null)
        onLabelDrop({ labelId: drag_labelId, parentId: null });
    }
}

function onDragOver(e) {
    e.dataTransfer.dropEffect = 'move';
    e.preventDefault();
}


async function addNewLabel() {
    if (newLabelName.value.trim()) {
        let parentId = null;
        if (selectedLabelId.value && selectedLabelId.value !== 'all' && selectedLabelId.value !== 'unlabeled') {
            parentId = selectedLabelId.value;
        }
        await imageStore.addLabel(newLabelName.value.trim(), parentId);
        newLabelName.value = '';
        labels.value = await imageStore.getAllLabels();
    }
}

async function deleteLabelById(id) {
    await imageStore.deleteLabelAndChildren(id);
    labels.value = await imageStore.getAllLabels();
}


</script>
